import { useState } from 'react';
import { Layers, Eye, EyeOff, GripVertical } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/Popover';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  useDashboardPreferencesActions,
  useDashboardPreferencesState,
} from '../../hooks/use-dashboard-preferences';
import { useReportConnections } from '../../hooks/reports/useReportConnections';
import { PLATFORM_ICONS, isComingSoonPlatform } from '../../constants/platform-config';
import { cn } from '../../lib/utils';

function SortableSectionRow({
  channelKey,
  platform,
  visible,
  connected,
  comingSoon,
  onToggle,
  onConnectClick,
}: {
  channelKey: string;
  platform: string;
  visible: boolean;
  connected: boolean;
  comingSoon: boolean;
  onToggle: () => void;
  onConnectClick: () => void;
}) {
  const disabled = comingSoon || !connected;

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: channelKey, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const icon = PLATFORM_ICONS[channelKey];

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-2 rounded-lg border px-3 py-2',
        disabled
          ? 'border-charcoalGray-50 bg-charcoalGray-50/40 opacity-60 dark:border-charcoalGray-500 dark:bg-charcoalGray-600/40'
          : visible
            ? 'border-primary-500/20 bg-primary-500/5'
            : 'border-charcoalGray-50 dark:border-charcoalGray-500',
      )}
    >
      <span
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        className={cn(disabled ? 'cursor-not-allowed' : 'cursor-grab')}
      >
        <GripVertical className="size-4 text-charcoalGray-300" />
      </span>

      {icon && <img src={icon} alt={platform} className={cn('size-5', disabled && 'grayscale')} />}

      <div className="flex flex-1 flex-col">
        <span className="flex items-center gap-1.5 text-sm text-charcoalGray-800 dark:text-white">
          {platform}
          {comingSoon && (
            <span className="rounded-full border border-primary-500/30 bg-primary-500/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-primary-700 dark:text-primary-300">
              Coming soon
            </span>
          )}
        </span>
        {!comingSoon && !connected && (
          <button
            type="button"
            onClick={onConnectClick}
            className="text-left text-[10px] text-charcoalGray-400 hover:text-primary-600 dark:text-charcoalGray-300"
          >
            Not connected — Go to Integrations
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={comingSoon ? undefined : connected ? onToggle : onConnectClick}
        disabled={disabled}
        title={
          comingSoon
            ? `${platform} support is coming soon`
            : connected
              ? undefined
              : 'Connect this platform to enable'
        }
        className={cn(
          'rounded p-1 transition-colors',
          disabled
            ? 'cursor-not-allowed text-charcoalGray-200 dark:text-charcoalGray-400'
            : visible
              ? 'text-primary-600 hover:bg-primary-500/10'
              : 'text-charcoalGray-300 hover:bg-charcoalGray-50 dark:hover:bg-charcoalGray-600',
        )}
      >
        {visible && !disabled ? <Eye className="size-4" /> : <EyeOff className="size-4" />}
      </button>
    </div>
  );
}

export function SectionEditor() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { activeView } = useDashboardPreferencesState();
  const { setSectionVisibility, reorderSections } = useDashboardPreferencesActions();
  const { isConnected } = useReportConnections();

  const sections = activeView.layoutSections;
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const goToIntegrations = () => {
    setOpen(false);
    navigate('/integrations');
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const keys = sections.map((s) => s.channelKey);
    const oldIndex = keys.indexOf(active.id as string);
    const newIndex = keys.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = [...keys];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved!);
    reorderSections(reordered);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-center rounded-lg border border-charcoalGray-50 p-2 text-charcoalGray-600 transition-colors hover:bg-charcoalGray-50 dark:border-charcoalGray-500 dark:text-charcoalGray-300 dark:hover:bg-charcoalGray-600"
          title="Edit sections"
        >
          <Layers className="size-4" />
        </button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-72 p-0">
        <div className="border-b border-charcoalGray-50 px-4 py-3 dark:border-charcoalGray-500">
          <h3 className="text-sm font-semibold text-charcoalGray-800 dark:text-white">Sections</h3>
          <p className="text-xs text-charcoalGray-400 dark:text-charcoalGray-200">
            Drag to reorder, toggle visibility
          </p>
        </div>

        <div className="space-y-1.5 p-3">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sections.map((s) => s.channelKey)}
              strategy={verticalListSortingStrategy}
            >
              {sections.map((s) => (
                <SortableSectionRow
                  key={s.channelKey}
                  channelKey={s.channelKey}
                  platform={s.platform}
                  visible={s.visible}
                  connected={isConnected(s.channelKey)}
                  comingSoon={isComingSoonPlatform(s.channelKey)}
                  onToggle={() => setSectionVisibility(s.channelKey, !s.visible)}
                  onConnectClick={goToIntegrations}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      </PopoverContent>
    </Popover>
  );
}
